import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Divider,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
// Date picker imports removed due to compatibility issues
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import CodeIcon from '@mui/icons-material/Code';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import ArticleIcon from '@mui/icons-material/Article';
import { useAuth } from '../../contexts/AuthContext';
import { PyStrategyEditor } from '../../components/strategy/PyStrategyEditor';
import { BacktestResults } from '../../components/strategy/BacktestResults';
import { backtestService, BacktestResult } from '../../services/backtestService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { TradingViewWidget } from '../../components/charts/TradingViewWidget';
import { VisualStrategyBuilder } from '../../components/strategy/VisualStrategyBuilder';

// Timeframe options for backtesting
const timeframeOptions = [
  { value: '1Min', label: '1 Minute' },
  { value: '5Min', label: '5 Minutes' },
  { value: '15Min', label: '15 Minutes' },
  { value: '30Min', label: '30 Minutes' },
  { value: '1Hour', label: '1 Hour' },
  { value: '1Day', label: '1 Day' },
  { value: '1Week', label: '1 Week' },
];

// Common stock symbols for the dropdown
const commonSymbols = [
  'SPY', 'QQQ', 'AAPL', 'MSFT', 'GOOG', 'AMZN', 'TSLA', 'META',
  'BTC/USD', 'ETH/USD', 'SOL/USD', 'ADA/USD'
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ height: '100%', pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const StrategyBuilderPage: React.FC = () => {
  const { id: strategyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useAuth();
  
  // Tab selection
  const [activeTab, setActiveTab] = useState(0);
  
  // Editor mode: 'code' or 'visual'
  const [editorMode, setEditorMode] = useState<'code' | 'visual'>('visual');
  
  // Strategy state
  const [strategyName, setStrategyName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Backtest parameters
  const [symbol, setSymbol] = useState('SPY');
  const [customSymbol, setCustomSymbol] = useState('');
  const [timeframe, setTimeframe] = useState('1Day');
  // Initialize with one year ago to current date
  const [startDate, setStartDate] = useState<string>(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [initialCapital, setInitialCapital] = useState(10000);
  
  // Results
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  
  // Deployment
  const [deployDialog, setDeployDialog] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState<any>(null);
  
  // Current strategy code
  const [code, setCode] = useState<string>('');

  // Load strategy details if editing
  useEffect(() => {
    if (strategyId && state.user) {
      loadStrategyDetails();
    }
  }, [strategyId, state.user]);

  const loadStrategyDetails = async () => {
    if (!state.user?.id || !strategyId) return;
    
    try {
      setLoading(true);
      const strategyDoc = await getDoc(doc(db, `users/${state.user.id}/strategies/${strategyId}`));
      
      if (strategyDoc.exists()) {
        const data = strategyDoc.data();
        setStrategyName(data.name || '');
        setDescription(data.description || '');
        setIsPublic(data.isPublic || false);
        
        // Set backtest parameters if they exist
        if (data.lastBacktest) {
          setSymbol(data.lastBacktest.symbol || 'SPY');
          setTimeframe(data.lastBacktest.timeframe || '1Day');
          setInitialCapital(data.lastBacktest.initialCapital || 10000);
          
          if (data.lastBacktest.startDate) {
            // Convert to ISO date string format YYYY-MM-DD
            setStartDate(new Date(data.lastBacktest.startDate).toISOString().split('T')[0]);
          }
          
          if (data.lastBacktest.endDate) {
            // Convert to ISO date string format YYYY-MM-DD
            setEndDate(new Date(data.lastBacktest.endDate).toISOString().split('T')[0]);
          }
        }
      } else {
        setError('Strategy not found');
      }
    } catch (error) {
      console.error('Error loading strategy details:', error);
      setError('Failed to load strategy details');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const updateStartDate = (date: Date) => {
    setStartDate(date.toISOString().split('T')[0]);
  };

  const updateEndDate = (date: Date) => {
    setEndDate(date.toISOString().split('T')[0]);
  };

  const handleStrategyChange = (newCode: string) => {
    setCode(newCode);
  };

  const handleStrategyRun = async (executionCode: string) => {
    if (!executionCode) {
      setBacktestError('No strategy code to run');
      return;
    }
    
    try {
      setBacktestLoading(true);
      setBacktestError(null);
      
      // Get the symbol to use (custom or selected)
      const symbolToUse = customSymbol || symbol;
      
      // Format dates
      const formattedStartDate = startDate ? startDate : '';
      const formattedEndDate = endDate ? endDate : '';
      
      // Run backtest with parameters
      const result = await backtestService.runBacktest({
        symbol: symbolToUse,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        interval: timeframe,
        initialCapital,
        code: executionCode
      });
      
      // Save the backtest parameters along with the strategy
      if (state.user?.id && strategyId) {
        await updateDoc(doc(db, `users/${state.user.id}/strategies/${strategyId}`), {
          lastBacktest: {
            symbol: symbolToUse,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            timeframe,
            initialCapital
          },
          lastBacktestResult: result
        });
      }
      
      setBacktestResult(result);
      
      // Switch to results tab
      setActiveTab(1);
    } catch (error) {
      console.error('Error running backtest:', error);
      setBacktestError('Failed to run backtest: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setBacktestLoading(false);
    }
  };

  const handleQuickRun = async () => {
    if (!code) {
      setBacktestError('No strategy code to run');
      return;
    }
    
    try {
      setBacktestLoading(true);
      setBacktestError(null);
      
      // Run a simplified backtest for quick feedback
      const result = await backtestService.runQuickBacktest(code, symbol);
      setBacktestResult(result);
      
      // Switch to results tab
      setActiveTab(1);
    } catch (error) {
      console.error('Error running quick backtest:', error);
      setBacktestError('Failed to run quick backtest: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setBacktestLoading(false);
    }
  };

  const handleSaveStrategy = async () => {
    if (!state.user?.id || !strategyId) return;
    
    try {
      setSaving(true);
      
      // Update the strategy in Firestore
      await updateDoc(doc(db, `users/${state.user.id}/strategies/${strategyId}`), {
        name: strategyName,
        description,
        isPublic,
        updatedAt: new Date()
      });
      
      // Show success notification
      alert('Strategy details saved successfully');
    } catch (error) {
      console.error('Error saving strategy details:', error);
      setError('Failed to save strategy details');
    } finally {
      setSaving(false);
    }
  };

  const handleDeploymentStart = () => {
    setDeployDialog(true);
  };

  const handleDeployment = async () => {
    if (!state.user?.id || !strategyId) {
      setError('You must be logged in and have a saved strategy to deploy');
      return;
    }
    
    try {
      setDeploying(true);
      
      // Deploy the strategy to Alpaca
      const result = await backtestService.deployToAlpaca(strategyId, state.user.id);
      
      // Update the strategy status in Firestore
      await updateDoc(doc(db, `users/${state.user.id}/strategies/${strategyId}`), {
        status: 'LIVE',
        deploymentId: result.deploymentId,
        deployedAt: new Date()
      });
      
      setDeploymentResult(result);
      setDeployDialog(false);
      
      // Redirect to strategy page or show success notification
      alert(`Strategy "${strategyName}" has been deployed successfully!`);
      navigate('/strategies');
    } catch (error) {
      console.error('Error deploying strategy:', error);
      setError('Failed to deploy strategy: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setDeploying(false);
    }
  };

  const effectiveSymbol = customSymbol || symbol;

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Strategy Name and Actions */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Strategy Name"
              variant="outlined"
              fullWidth
              size="small"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={handleQuickRun}
                disabled={backtestLoading}
              >
                Quick Run
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveStrategy}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Strategy'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={handleDeploymentStart}
                disabled={!backtestResult || deploying}
              >
                Deploy to Alpaca
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Left Column: Editor */}
        <Grid item xs={12} lg={7} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Editor" />
              <Tab label="Results" />
              <Tab label="Chart" />
            </Tabs>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <TabPanel value={activeTab} index={0}>
              {editorMode === 'code' ? (
                <PyStrategyEditor 
                  strategyId={strategyId}
                  onSave={(id, newCode) => {
                    setCode(newCode);
                    navigate(`/strategy/${id}`);
                  }}
                  onRun={handleStrategyRun}
                />
              ) : (
                <VisualStrategyBuilder 
                  onSave={(rules) => {
                    // Here you would convert the visual rules to Python code
                    // For now, this is a placeholder
                    const generatedCode = `# Generated from Visual Strategy Builder\n\n# Import libraries\nimport pandas as pd\nimport numpy as np\n\n# Define your strategy function\ndef run_strategy(data):\n    # Initialize results\n    signals = []\n    portfolio = {'cash': 10000, 'positions': {}}\n    \n    # Apply strategy rules\n    for index, row in data.iterrows():\n        # YOUR VISUAL RULES CONVERTED TO PYTHON CODE\n        # Example: if BTC price crosses above 50000, buy 0.1 BTC\n        pass\n    \n    return {'signals': signals, 'portfolio': portfolio}\n`;
                    
                    setCode(generatedCode);
                    // Optionally switch to code view to show generated code
                    // setEditorMode('code');
                  }}
                  onTest={() => {
                    // Convert visual rules to code and then run the backtest
                    handleStrategyRun(code);
                  }}
                />
              )}
            </TabPanel>
            
            <TabPanel value={activeTab} index={1}>
              <BacktestResults
                result={backtestResult}
                loading={backtestLoading}
                error={backtestError}
              />
            </TabPanel>
            
            <TabPanel value={activeTab} index={2}>
              <Paper elevation={3} sx={{ height: '100%', p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {effectiveSymbol} Chart
                </Typography>
                <Box sx={{ height: 'calc(100% - 40px)' }}>
                  <TradingViewWidget
                    symbol={effectiveSymbol}
                    theme="dark"
                    autosize
                  />
                </Box>
              </Paper>
            </TabPanel>
          </Box>
        </Grid>
        
        {/* Right Column: Parameters */}
        <Grid item xs={12} lg={5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Backtest Parameters
            </Typography>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Symbol"
                  fullWidth
                  value={symbol}
                  onChange={(e) => {
                    setSymbol(e.target.value);
                    if (e.target.value === 'custom') {
                      setCustomSymbol('');
                    }
                  }}
                >
                  {commonSymbols.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">Custom Symbol</MenuItem>
                </TextField>
              </Grid>
              
              {symbol === 'custom' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Custom Symbol"
                    fullWidth
                    value={customSymbol}
                    onChange={(e) => setCustomSymbol(e.target.value)}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Timeframe"
                  fullWidth
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                >
                  {timeframeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Initial Capital"
                  fullWidth
                  type="number"
                  value={initialCapital}
                  onChange={(e) => setInitialCapital(Number(e.target.value))}
                  InputProps={{
                    startAdornment: <Typography variant="body1">$</Typography>
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleStrategyRun(code)}
                  disabled={backtestLoading}
                >
                  {backtestLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Run Full Backtest'
                  )}
                </Button>
              </Grid>

              {/* Documentation Links */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Resources:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      color="info"
                      startIcon={<ArticleIcon />}
                      href="https://www.alpaca.markets/docs/api-references/trading-api"
                      target="_blank"
                    >
                      Alpaca API Documentation
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="info"
                      startIcon={<ArticleIcon />}
                      href="https://pandas.pydata.org/docs/"
                      target="_blank"
                    >
                      Pandas Documentation
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="info"
                      startIcon={<ArticleIcon />}
                      href="https://numpy.org/doc/"
                      target="_blank"
                    >
                      NumPy Documentation
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Deployment Dialog */}
      <Dialog open={deployDialog} onClose={() => setDeployDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deploy Strategy to Alpaca</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to deploy your strategy "{strategyName}" to Alpaca.
          </Typography>
          <Typography variant="body2" gutterBottom>
            This will make your strategy live and it will begin executing trades based on your rules.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Make sure your strategy has been thoroughly tested before deployment. Live trading involves real financial risk.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeployDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeployment} 
            variant="contained" 
            color="primary"
            disabled={deploying}
          >
            {deploying ? <CircularProgress size={24} /> : 'Deploy to Alpaca'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrategyBuilderPage;
